import { useMemo } from "react";
import useSWR from "swr";
import { AgentService } from "@/services/agent/agent.service";
import { defaultSWROption } from "@/services/api/api.service";

interface FilesByFolder {
  [folder: string]: string[];
}

export const useSandboxFiles = (threadPath: string | null) => {
  // /workspace/data のファイルを取得 (dataは全スレッドで表示)
  const dataFetcher = async () => {
    return await AgentService.listSandboxFiles("/workspace/data");
  };

  const {
    data: dataData,
    error: dataError,
    isLoading: isLoadingData,
    isValidating: isValidatingData,
    mutate: mutateData,
  } = useSWR(
    threadPath ? ["agent", "sandboxFiles", "/workspace/data"] : null,
    dataFetcher,
    defaultSWROption,
  );

  // threadPathのファイルを取得
  const threadFetcher = async () => {
    if (!threadPath) return [];
    return await AgentService.listSandboxFiles(threadPath);
  };

  const {
    data: threadData,
    error: threadError,
    isLoading: isLoadingThread,
    isValidating: isValidatingThread,
    mutate: mutateThread,
  } = useSWR(
    threadPath ? ["agent", "sandboxFiles", threadPath] : null,
    threadFetcher,
    defaultSWROption,
  );

  // エラーとローディング状態を統合
  const error = threadError || dataError;
  const isLoading = isLoadingThread || isLoadingData;
  const isValidating = isValidatingThread || isValidatingData;

  // 両方のmutateを呼び出す関数
  const mutate = async () => {
    await Promise.all([mutateThread(), mutateData()]);
  };

  // 2つのデータソースを統合
  const combinedData = useMemo(() => {
    const thread = threadData || [];
    const data = dataData || [];
    return [...data, ...thread];
  }, [dataData, threadData]);

  // フォルダを除外したファイル一覧
  const files = useMemo(() => {
    if (!combinedData) return [];
    return combinedData.filter((filePath) => !["/workspace", threadPath, "/workspace/data"].includes(filePath));
  }, [combinedData, threadPath]);

  // ファイル名を取得（パスの最後の部分）
  const getFileName = (filePath: string): string => {
    return filePath.substring(filePath.lastIndexOf("/") + 1);
  };

  // ファイルかフォルダかを判定（ファイル名に`.`が含まれていればファイル）
  const isFile = (filePath: string): boolean => {
    const fileName = getFileName(filePath);
    return fileName.includes(".");
  };

  // ファイルをフォルダごとにグループ化
  const filesByFolder = useMemo<FilesByFolder>(() => {
    const grouped: FilesByFolder = {};

    files.forEach((filePath) => {
      // フォルダパスを取得（ファイル名を除く）
      const folderPath = filePath.includes("/")
        ? filePath.substring(0, filePath.lastIndexOf("/"))
        : "/";

      if (!grouped[folderPath]) {
        grouped[folderPath] = [];
      }
      grouped[folderPath].push(filePath);
    });

    return grouped;
  }, [files]);

  // ソート済みフォルダパス (/workspace/dataを先に表示)
  const folderPaths = useMemo(() => {
    return Object.keys(filesByFolder).sort((a, b) => {
      const aIsData = a.startsWith("/workspace/data");
      const bIsData = b.startsWith("/workspace/data");
      if (aIsData && !bIsData) return -1;
      if (!aIsData && bIsData) return 1;
      return a.localeCompare(b);
    });
  }, [filesByFolder]);

  // ローディング状態
  const isRefreshing = isLoading || isValidating;
  const hasLoaded = files.length > 0 || !isLoading;

  return {
    files,
    filesByFolder,
    folderPaths,
    error,
    isLoading,
    isValidating,
    isRefreshing,
    hasLoaded,
    mutate,
    // ヘルパー関数
    getFileName,
    isFile,
  };
};
